import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { postNewsletter } from "../lib/api";
import { trackEvent } from "./Analytics";
import { CTAButton } from "./CTAButton";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await postNewsletter(email);
      toast.success("Subscribed — check your inbox for Investor Insights.");
      trackEvent("newsletter_signup", { location: "footer" });
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="border-t border-border bg-primary text-primary-foreground">
      <div className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10 lg:py-24">
        <div className="grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <span className="overline text-accent-foreground/60">Investor Insights</span>
            <h2 className="mt-4 max-w-md font-serif text-3xl font-light leading-tight sm:text-4xl">
              Massachusetts rent trends &amp; owner strategy — straight to your inbox.
            </h2>
            <form onSubmit={subscribe} className="mt-8 flex max-w-md border border-primary-foreground/30">
              <input
                data-testid="footer-newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-transparent px-4 py-3.5 text-sm text-primary-foreground placeholder:text-primary-foreground/40 focus:outline-none"
              />
              <button
                data-testid="footer-newsletter-submit"
                disabled={loading}
                className="whitespace-nowrap bg-accent px-6 text-sm font-medium text-accent-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {loading ? "..." : "Subscribe"}
              </button>
            </form>
          </div>

          <div>
            <span className="overline text-primary-foreground/50">Explore</span>
            <ul className="mt-5 space-y-3 text-sm text-primary-foreground/80">
              {[
                ["/about", "About"],
                ["/services", "Services"],
                ["/rental-analysis", "Rental Analysis"],
                ["/areas", "Areas We Serve"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link to={to} className="link-underline" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <span className="overline text-primary-foreground/50">Resources</span>
            <ul className="mt-5 space-y-3 text-sm text-primary-foreground/80">
              <li><Link to="/owner-faq" className="link-underline">Owner FAQ</Link></li>
              <li><Link to="/resident-faq" className="link-underline">Resident FAQ</Link></li>
              <li><Link to="/contact" className="link-underline">Book a Consultation</Link></li>
            </ul>
            <div className="mt-8 space-y-1 text-sm text-primary-foreground/70">
              <p>(857) 506-3391</p>
              <p>hello@cummingsandcopm.com</p>
              <p>Boston, Massachusetts</p>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-primary-foreground/15 pt-8 sm:flex-row sm:items-center">
          <img src="/logo.png" alt="Cummings & Co. Property Management" className="h-12 w-auto brightness-0 invert" />
          <p className="text-xs text-primary-foreground/50">
            © {new Date().getFullYear()} Cummings &amp; Co. — Maximizing returns, minimizing stress.
          </p>
          <CTAButton to="/contact" variant="accent" className="px-5 py-2.5 text-xs" testid="footer-cta">Get Started</CTAButton>
        </div>
      </div>
    </footer>
  );
};
